import { API_CACHE_KEYS } from "api/cacheKeys";
import { ApiError, BaseQueryOptions, CachePolicy } from "api/types";
import { useCustomMutation } from "api/useCustomMutation";
import { useCustomQuery } from "api/useCustomQuery";
import { JANZU_URL } from "config/config";
import { ListResponse } from "types/api";

export interface ApiIdentityResponse {
  id: string;
  name: string;
  created_at: string;
  revoked_at: string | null;
  /** Token lifetime policy in seconds; null means never-expiring minted tokens. */
  token_ttl: number | null;
  expires_at: string | null;
  last_used_at: string | null;
  token_count: number;
}

export interface ApiIdentityCreationResponse extends ApiIdentityResponse {
  raw_key: string;
  /** Expiry of the first minted secret; distinct from identity expires_at. */
  token_expires_at: string | null;
}

export type CreateApiIdentityData = {
  name: string;
  /** Phase 1 interim: omit or null = never-expire; seconds otherwise. */
  token_ttl?: number | null;
  expires_at?: string | null;
};

export type GetApiIdentitiesParams = {
  pageSize?: number;
  page?: number;
  ordering?: string;
  search?: string;
  includeRevoked?: boolean;
  url?: string | null;
};

type UseGetApiIdentitiesParams = {
  params?: GetApiIdentitiesParams;
  options?: BaseQueryOptions<ListResponse<ApiIdentityResponse>>;
};

function useGetApiIdentities({ params, options }: UseGetApiIdentitiesParams = {}) {
  const searchParams = new URLSearchParams();
  if (!params?.url) {
    if (params?.pageSize !== undefined) {
      searchParams.append("page_size", params.pageSize.toString());
    }
    if (params?.page !== undefined) {
      searchParams.append("page", params.page.toString());
    }
    if (params?.ordering !== undefined) {
      searchParams.append("ordering", params.ordering);
    }
    if (params?.search) {
      searchParams.append("search", params.search);
    }
    if (params?.includeRevoked) {
      searchParams.append("include_revoked", "true");
    }
  }

  const finalUrl = params?.url || `${JANZU_URL}device-api/api-identities?${searchParams.toString()}`;

  return useCustomQuery<ListResponse<ApiIdentityResponse>>(API_CACHE_KEYS.apiIdentities.list(params), finalUrl, {
    cachePolicy: CachePolicy.NO_CACHE,
    // List data is owned by Redux; only fetch via React Query when explicitly enabled.
    enabled: false,
    ...options,
  });
}

function useCreateApiIdentity() {
  return useCustomMutation<ApiIdentityCreationResponse, CreateApiIdentityData>(
    `${JANZU_URL}device-api/api-identities`,
    "POST",
    // CreateForm shows a friendly snackbar; avoid a second automatic API error toast.
    { shouldShowErrorSnackbar: () => false }
  );
}

function useRevokeApiIdentity() {
  return useCustomMutation<ApiIdentityResponse, { id: string }>(
    (variables) => `${JANZU_URL}device-api/api-identities/${variables.id}/revoke`,
    "POST",
    // ApiTokenTableList shows its own revoke-failure snackbar.
    { shouldShowErrorSnackbar: () => false }
  );
}

type UseApiIdentitiesParams = {
  getOptions?: {
    params?: GetApiIdentitiesParams;
    options?: BaseQueryOptions<ListResponse<ApiIdentityResponse>>;
  };
};

type UseApiIdentitiesReturn = {
  apiIdentities: ListResponse<ApiIdentityResponse> | undefined;
  isLoadingApiIdentities: boolean;
  apiIdentitiesError: ApiError | null;
  refetchApiIdentities: () => Promise<ListResponse<ApiIdentityResponse>>;

  createApiIdentity: (data: CreateApiIdentityData) => void;
  createApiIdentityAsync: (data: CreateApiIdentityData) => Promise<ApiIdentityCreationResponse>;
  isCreatingApiIdentity: boolean;
  createApiIdentityError: ApiError | null;
  createdApiIdentity: ApiIdentityCreationResponse | undefined;

  revokeApiIdentity: (id: string) => void;
  revokeApiIdentityAsync: (id: string) => Promise<ApiIdentityResponse>;
  isRevokingApiIdentity: boolean;
  revokeApiIdentityError: ApiError | null;

  isLoading: boolean;
};

/** React Query hooks for device-api ApiIdentity list/create/revoke. */
export function useApiIdentities({ getOptions }: UseApiIdentitiesParams = {}): UseApiIdentitiesReturn {
  const getApiIdentitiesQuery = useGetApiIdentities({
    params: getOptions?.params,
    options: getOptions?.options,
  });
  const createApiIdentityMutation = useCreateApiIdentity();
  const revokeApiIdentityMutation = useRevokeApiIdentity();

  return {
    apiIdentities: getApiIdentitiesQuery.data,
    isLoadingApiIdentities: getApiIdentitiesQuery.isFetching,
    apiIdentitiesError: getApiIdentitiesQuery.error,
    refetchApiIdentities: getApiIdentitiesQuery.refetch,

    createApiIdentity: (data: CreateApiIdentityData) => createApiIdentityMutation.mutate(data),
    createApiIdentityAsync: (data: CreateApiIdentityData) => createApiIdentityMutation.mutateAsync(data),
    isCreatingApiIdentity: createApiIdentityMutation.isLoading,
    createApiIdentityError: createApiIdentityMutation.error,
    createdApiIdentity: createApiIdentityMutation.data,

    revokeApiIdentity: (id: string) => revokeApiIdentityMutation.mutate({ id }),
    revokeApiIdentityAsync: (id: string) => revokeApiIdentityMutation.mutateAsync({ id }),
    isRevokingApiIdentity: revokeApiIdentityMutation.isLoading,
    revokeApiIdentityError: revokeApiIdentityMutation.error,

    isLoading:
      getApiIdentitiesQuery.isFetching ||
      createApiIdentityMutation.isLoading ||
      revokeApiIdentityMutation.isLoading,
  };
}
